/**
 * AccessCodesSection — driver-side container that fetches and displays
 * access codes for one delivery stop.
 *
 * Phase 2 (Gate Codes). Calls /api/access-codes/lookup on mount per stop and
 * renders zero or more compact AccessCodeBadge lines. No reporting / deprecate
 * flow lives here anymore — that moved to AccessCodeDetail inside the
 * DeliveryConfirmationSheet, and the multi-code REPORT CODE picker page is
 * tracked separately as P2.4b.
 *
 * Renders nothing when no codes apply and the community is not guard-gated.
 * Loading and failed lookups remain distinct from confirmed empty results.
 *
 * Props:
 *   orderId: string — the order whose place_id we should match against.
 */
import React from 'react';
import { UserRound } from 'lucide-react';
import { useAccessCodeLookup } from '@/hooks/useAccessCodeLookup';
import AccessCodeLookupStatus from '@/components/AccessCodeLookupStatus';
import AccessCodeBadge from './AccessCodeBadge';

export default function AccessCodesSection({ orderId, openingId, addressKey }) {
  const lookup = useAccessCodeLookup({ orderId, openingId, addressKey });
  const { codes, guardGated, status } = lookup;
  if (!orderId || (status === 'ready' && !codes.length && !guardGated)) return null;

  // Gate codes first, then doors. Stable enough for the driver to glance.
  const sortedCodes = [...codes].sort((a, b) => {
    if (a.type === b.type) return 0;
    return a.type === 'gate' ? -1 : 1;
  });

  return (
    <div data-testid={`access-codes-section-${orderId}`}>
      <AccessCodeLookupStatus lookup={lookup} testId={`access-codes-status-${orderId}`} />
      {guardGated && (
        <div
          className="mt-1.5 flex items-center gap-1.5 text-sm text-stone-700"
          data-testid={`guard-gated-badge-${orderId}`}
        >
          <UserRound className="h-4 w-4 text-amber-700 flex-shrink-0" />
          <span className="font-medium text-stone-700">Guard</span>
        </div>
      )}
      {sortedCodes.map((code, idx) => (
        <AccessCodeBadge
          key={code.id}
          code={code}
          index={idx}
          total={sortedCodes.length}
        />
      ))}
    </div>
  );
}
